import axios from 'axios'
import { appStore } from '@/store/app'
import { storeToRefs } from 'pinia'
import { cesiumConfig } from '@/config/cesium.config'
import log from '@/utils/useLogger'
import { computed, ref, watch } from 'vue'

const isUserLocationFetched = ref(false)
const isFetchingUserLocation = ref(false)
const userLocation = ref<{ longitude: number; latitude: number } | null>(null)
// Module-level guards so the reverse-geocode only fires once per location
// change, even though useDroneLocation is instantiated in many components.
// `requestedGeocodeKey` tracks the in-flight request; `lastGeocodedKey` only
// advances after a successful response so transient failures stay retryable.
const requestedGeocodeKey = ref<string | null>(null)
const lastGeocodedKey = ref<string | null>(null)
const useDroneLocation = () => {
  const appInstance = appStore()
  const { telemetry, dockConfigurations, cloudAddress } = storeToRefs(appInstance)

  // Computed property that reactively returns drone location
  const droneLocation = computed(() => {
    // 1. Use telemetry if available
    if (
      telemetry.value?.locationNavigation?.longitude &&
      telemetry.value?.locationNavigation?.latitude &&
      telemetry.value.locationNavigation.longitude !== 0.0 &&
      telemetry.value.locationNavigation.latitude !== 0.0
    ) {
      return {
        longitude: Number(telemetry.value.locationNavigation.longitude),
        latitude: Number(telemetry.value.locationNavigation.latitude),
      }
    }

    // 2. Use dock configuration if available
    if (dockConfigurations.value?.dockLongitude && dockConfigurations.value?.dockLatitude) {
      return {
        longitude: Number(dockConfigurations.value.dockLongitude),
        latitude: Number(dockConfigurations.value.dockLatitude),
      }
    }

    // 3. Use user geolocation if available
    if (userLocation.value) return userLocation.value

    // 4. Fallback to HQ location
    return {
      longitude: Number(cesiumConfig.defaultDronePosition.longitude),
      latitude: Number(cesiumConfig.defaultDronePosition.latitude),
    }
  })

  const fetchUserLocation = async (): Promise<{ longitude: number; latitude: number }> => {
    try {
      isFetchingUserLocation.value = true
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        log.info('fetching user location')
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              userLocation.value = {
                longitude: Number(position.coords.longitude),
                latitude: Number(position.coords.latitude),
              }
              isUserLocationFetched.value = true
              resolve({
                longitude: Number(position.coords.longitude),
                latitude: Number(position.coords.latitude),
              })
            },
            (error) => {
              log.error('Geolocation error:', error.message)
              userLocation.value = null
              isUserLocationFetched.value = true
              resolve({
                longitude: Number(cesiumConfig.defaultDronePosition.longitude),
                latitude: Number(cesiumConfig.defaultDronePosition.latitude),
              })
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            },
          )
        })
      } else {
        log.warn('Geolocation is not supported by this browser.')
        isUserLocationFetched.value = true
        return {
          longitude: Number(cesiumConfig.defaultDronePosition.longitude),
          latitude: Number(cesiumConfig.defaultDronePosition.latitude),
        }
      }
    } catch (error) {
      log.error('Error fetching user location', error)
      return {
        longitude: Number(cesiumConfig.defaultDronePosition.longitude),
        latitude: Number(cesiumConfig.defaultDronePosition.latitude),
      }
    } finally {
      isFetchingUserLocation.value = false
    }
  }
  const hasTelemetryLocation = computed(() => {
    return (
      telemetry.value?.locationNavigation?.longitude &&
      telemetry.value?.locationNavigation?.latitude
    )
  })
  const hasDockLocation = computed(() => {
    return dockConfigurations.value?.dockLongitude && dockConfigurations.value?.dockLatitude
  })
  watch(
    [hasTelemetryLocation, hasDockLocation],
    async () => {
      if (hasTelemetryLocation.value || hasDockLocation.value || isUserLocationFetched.value) return
      log.info('No telemetry or dock location available, fetching user location')
      if (!isFetchingUserLocation.value) {
        await fetchUserLocation()
      }
    },
    { immediate: true },
  )

  const hasResolvedLocation = computed(
    () => !!(hasTelemetryLocation.value || hasDockLocation.value || userLocation.value),
  )

  // Reverse-geocode the resolved drone location into a human-readable address.
  // `key` identifies the request; we ignore responses that a newer request has
  // already superseded, and only advance lastGeocodedKey on success.
  const fetchDroneAddress = async (longitude: number, latitude: number, key: string) => {
    try {
      const token = import.meta.env.VITE_APP_KEY
      const url = `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${longitude}&latitude=${latitude}&access_token=${token}`
      const res = await axios.get(url)
      // Drop stale/out-of-order responses so they can't overwrite a newer one.
      if (key !== requestedGeocodeKey.value) return
      if (res.data && res.data.features.length > 0) {
        cloudAddress.value = res.data.features[0].properties.full_address
      } else {
        cloudAddress.value = 'Unknown location'
      }
      lastGeocodedKey.value = key
    } catch (error) {
      log.error('Error fetching drone address', error)
      if (key === requestedGeocodeKey.value) requestedGeocodeKey.value = null
    }
  }

  // Refetch the address only when the location changes to ~4-decimal precision
  // (~11m). The module-level keys dedupe across the many useDroneLocation
  // instances so we issue a single geocode request per real location change,
  // skipping both already-resolved locations and one currently in flight.
  watch(
    droneLocation,
    (loc) => {
      if (!hasResolvedLocation.value || !loc) return
      const lng = Number(loc.longitude)
      const lat = Number(loc.latitude)
      if (!lng || !lat) return
      const key = `${lng.toFixed(4)},${lat.toFixed(4)}`
      if (key === lastGeocodedKey.value || key === requestedGeocodeKey.value) return
      requestedGeocodeKey.value = key
      fetchDroneAddress(lng, lat, key)
    },
    { immediate: true },
  )

  return {
    droneLocation,
    fetchUserLocation,
    fetchDroneAddress,
    userLocation,
  }
}
export default useDroneLocation
